'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const patientServices = [
  { label: 'Dialysis Transportation', href: '/services/dialysis-transportation' },
  { label: 'Wheelchair Transportation', href: '/services/wheelchair-transportation' },
  { label: 'Stretcher Transportation', href: '/services/stretcher-transportation' },
  { label: 'Medicaid Transportation', href: '/services/medicaid-transportation' },
];

const facilityServices = [
  { label: 'Hospital Discharge', href: '/services/hospital-discharge' },
  { label: 'Nursing Home Transportation', href: '/services/nursing-home-transportation' },
  { label: 'Assisted Living Transportation', href: '/services/assisted-living-transportation' },
  { label: 'Hospice Transportation', href: '/services/hospice-transportation' },
];

const navLinks = [
  { label: 'Services', href: '#', hasDropdown: true },
  { label: 'For Facilities', href: '/facilities' },
  { label: 'About', href: '/about' },
  { label: 'Careers', href: '/careers' },
  { label: 'Contact', href: '/contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white shadow-md py-3'
            : 'bg-transparent py-5'
        }`}
        role="banner"
      >
        <div className="max-w-screen-xl mx-auto px-6 md:px-10 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 group" aria-label="Kapra Medical Transportation — Home">
            <AppLogo size={36} className="shrink-0" />
            <span className={`font-display font-semibold text-lg leading-tight hidden sm:block ${scrolled ? 'text-gray-900' : 'text-primary-foreground'}`}>
              Kapra<br />
              <span className="text-accent text-sm font-sans font-medium tracking-wide">Medical Transportation</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary navigation">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div key={link.label} className="relative" ref={dropdownRef}>
                  <button
                    className={`flex items-center gap-1.5 text-sm font-medium transition-colors py-1 ${
                      scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/80 hover:text-white'
                    }`}
                    onClick={() => setDropdownOpen((p) => !p)}
                    aria-expanded={dropdownOpen}
                    aria-haspopup="true"
                  >
                    {link.label}
                    <Icon
                      name="ChevronDownIcon"
                      size={14}
                      className={`transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {dropdownOpen && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[480px] bg-card rounded-xl shadow-card-hover border border-border overflow-hidden z-50">
                      {/* Two-column layout */}
                      <div className="grid grid-cols-2 gap-0">
                        {/* Column 1 — For Patients & Families */}
                        <div className="border-r border-border">
                          <div className="px-4 py-2.5 bg-muted border-b border-border">
                            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">For Patients &amp; Families</span>
                          </div>
                          {patientServices.map((s) => (
                            <Link
                              key={s.href}
                              href={s.href}
                              className="block px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted hover:text-primary transition-colors border-b border-border last:border-0"
                              onClick={() => setDropdownOpen(false)}
                            >
                              {s.label}
                            </Link>
                          ))}
                        </div>
                        {/* Column 2 — For Facilities */}
                        <div>
                          <div className="px-4 py-2.5 bg-muted border-b border-border">
                            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">For Facilities</span>
                          </div>
                          {facilityServices.map((s) => (
                            <Link
                              key={s.href}
                              href={s.href}
                              className="block px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted hover:text-primary transition-colors border-b border-border last:border-0"
                              onClick={() => setDropdownOpen(false)}
                            >
                              {s.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                      {/* Bottom links */}
                      <div className="border-t border-border bg-muted/50 flex items-center justify-between px-4 py-2.5 gap-4">
                        <Link
                          href="/medical-transportation-eligibility"
                          className="text-xs font-semibold text-primary hover:text-accent transition-colors"
                          onClick={() => setDropdownOpen(false)}
                        >
                          Who Qualifies? →
                        </Link>
                        <Link
                          href="/services"
                          className="text-xs font-semibold text-primary hover:text-accent transition-colors"
                          onClick={() => setDropdownOpen(false)}
                        >
                          View all services →
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.label}
                  href={link.href}
                  className={`text-sm font-medium transition-colors ${
                    scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/80 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          {/* CTA */}
          <div className="flex items-center gap-3">
            <a
              href="tel:+15513269573"
              className="btn-primary text-sm py-2.5 px-5 hidden sm:inline-flex"
              aria-label="Call Kapra Medical Transportation at (551) 326-9573"
            >
              <Icon name="PhoneIcon" size={15} />
              <span className="hidden md:inline">(551) 326-9573</span>
              <span className="md:hidden">Call Now</span>
            </a>

            {/* Hamburger */}
            <button
              className="lg:hidden flex flex-col gap-1.5 p-2 group"
              onClick={() => setMenuOpen((p) => !p)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
            >
              <span
                className={`block w-6 h-0.5 transition-all duration-300 ${scrolled ? 'bg-gray-800' : 'bg-white'} ${
                  menuOpen ? 'rotate-45 translate-y-2' : ''
                }`}
              />
              <span
                className={`block h-0.5 transition-all duration-300 ${scrolled ? 'bg-gray-800' : 'bg-white'} ${
                  menuOpen ? 'opacity-0 w-0' : 'w-4'
                }`}
              />
              <span
                className={`block w-6 h-0.5 transition-all duration-300 ${scrolled ? 'bg-gray-800' : 'bg-white'} ${
                  menuOpen ? '-rotate-45 -translate-y-2' : ''
                }`}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          id="mobile-menu"
          className="fixed inset-0 z-40 bg-primary/98 backdrop-blur-md flex flex-col pt-24 pb-10 px-6 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
        >
          <nav className="flex flex-col gap-2" aria-label="Mobile navigation links">
            {/* Patients & Families */}
            <div className="mb-2">
              <p className="text-xs font-bold uppercase tracking-widest text-white/40 mb-3 px-2">For Patients &amp; Families</p>
              {patientServices.map((s) => (
                <Link
                  key={s.href}
                  href={s.href}
                  className="block px-2 py-3 text-base font-medium text-white/80 hover:text-white border-b border-white/10 transition-colors"
                  onClick={() => setMenuOpen(false)}
                >
                  {s.label}
                </Link>
              ))}
            </div>
            {/* Facilities */}
            <div className="mb-2">
              <p className="text-xs font-bold uppercase tracking-widest text-white/40 mb-3 px-2">For Facilities</p>
              {facilityServices.map((s) => (
                <Link
                  key={s.href}
                  href={s.href}
                  className="block px-2 py-3 text-base font-medium text-white/80 hover:text-white border-b border-white/10 transition-colors"
                  onClick={() => setMenuOpen(false)}
                >
                  {s.label}
                </Link>
              ))}
            </div>
            {/* Other nav links */}
            {navLinks
              .filter((l) => !l.hasDropdown)
              .map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  className="block px-2 py-3 text-lg font-medium text-white/80 hover:text-white border-b border-white/10 transition-colors"
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            <Link
              href="/medical-transportation-eligibility"
              className="block px-2 py-3 text-base font-medium text-accent/80 hover:text-accent border-b border-white/10 transition-colors"
              onClick={() => setMenuOpen(false)}
            >
              Who Qualifies?
            </Link>
          </nav>
          <div className="mt-auto pt-8 flex flex-col gap-3">
            <a
              href="tel:+15513269573"
              className="btn-primary justify-center text-base py-4"
              onClick={() => setMenuOpen(false)}
            >
              <Icon name="PhoneIcon" size={18} />
              Call (551) 326-9573
            </a>
            <Link
              href="/contact"
              className="btn-secondary justify-center text-base py-4"
              onClick={() => setMenuOpen(false)}
            >
              Request Transport
            </Link>
          </div>
        </div>
      )}
    </>
  );
}